import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from './api';

export interface CategoryResolutionInput {
  term: string;
  action: 'map' | 'skip';
  category_id?: number | null;
  new_category_name?: string | null;
  new_category_parent_id?: number | null;
}

export interface CategoryResolutionOutcome {
  term: string;
  action: 'mapped' | 'created_and_mapped' | 'skipped';
  category_id: number | null;
  parts_updated: number;
}

export interface BulkResolutionResponse {
  outcomes: CategoryResolutionOutcome[];
}

/**
 * POST a batch of unmapped-category resolutions. The user picks one
 * resolution per unrecognized term in the import wizard's last step,
 * and we send them all in one transaction. Backend creates new
 * categories as needed, persists per-org aliases, and back-fills
 * existing parts whose subcategory text matches.
 */
export function useBulkResolveCategories(orgSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (resolutions: CategoryResolutionInput[]) =>
      api.postJson<BulkResolutionResponse>(
        `/api/orgs/${orgSlug}/category-aliases/bulk`,
        { resolutions },
      ),
    onSuccess: () => {
      // Just-resolved parts may have a new category_id, and the
      // resolution may have created new global categories. Refresh
      // the parts list and the categories cache.
      qc.invalidateQueries({ queryKey: ['parts', orgSlug] });
      qc.invalidateQueries({ queryKey: ['part-categories'] });
    },
  });
}
